import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Copy } from "lucide-react";
import { DashboardLayout } from "../../components/DashboardLayout";
import { StatCard, StatusBadge, money } from "../../components/DashWidgets";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { LOAN_TYPES } from "../../lib/data";
import { agentAddClient, agentApplications, agentCommissions, agentTeam } from "../../lib/api";
import { TeamTree } from "../../components/TeamTree";
import { PayoutsPanel } from "../../components/PayoutsPanel";
import { useTabParam } from "../../lib/useTabParam";

export default function AgentDashboard() {
  const [tab, setTab] = useTabParam("overview");
  const [apps, setApps] = useState([]);
  const [comm, setComm] = useState({ commissions: [], total: 0, paid: 0, pending: 0 });
  const [team, setTeam] = useState({ downline: [], count: 0, referral_code: "" });

  const load = async () => {
    const [a, c, t] = await Promise.all([agentApplications(), agentCommissions(), agentTeam()]);
    setApps(a); setComm(c); setTeam(t);
  };
  useEffect(() => { load(); }, []);

  return (
    <DashboardLayout tab={tab} setTab={setTab}>
      {tab === "overview" && (
        <div className="space-y-6" data-testid="agent-overview">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon="FileText" label="Client Applications" value={apps.length} testid="agent-stat-apps" />
            <StatCard icon="Coins" label="Total Earnings" value={money(comm.total)} accent="green" testid="agent-stat-total" />
            <StatCard icon="Clock" label="Pending" value={money(comm.pending)} testid="agent-stat-pending" />
            <StatCard icon="Network" label="Team Members" value={team.count} testid="agent-stat-team" />
          </div>
          <div className="card-dark rounded-2xl p-6">
            <div className="text-xs uppercase tracking-widest text-gold font-semibold">Your Referral Code</div>
            <div className="font-mono-fin text-2xl font-extrabold text-white mt-1">{team.referral_code}</div>
          </div>
        </div>
      )}
      {tab === "add" && <AddClient onDone={() => { load(); setTab("applications"); }} />}
      {tab === "applications" && (
        <div data-testid="agent-applications">
          <h2 className="font-heading text-2xl font-bold text-white mb-5">Client Applications</h2>
          {apps.length === 0 ? <p className="text-slate-400">No client applications yet.</p> : (
            <div className="space-y-3">
              {apps.map((a) => (
                <div key={a.id} className="card-dark rounded-xl p-5 flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="font-semibold text-white">{a.customer_name} <span className="text-slate-500 text-sm">· {a.customer_phone}</span></div>
                    <div className="text-slate-400 text-sm">{a.loan_type} · <span className="font-mono-fin text-gold">{money(a.amount)}</span></div>
                  </div>
                  <StatusBadge status={a.status} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {tab === "commissions" && (
        <div data-testid="agent-commissions">
          <h2 className="font-heading text-2xl font-bold text-white mb-5">My Commissions</h2>
          <div className="grid grid-cols-3 gap-4 mb-6">
            <StatCard icon="Coins" label="Total" value={money(comm.total)} accent="green" />
            <StatCard icon="CheckCircle2" label="Paid" value={money(comm.paid)} accent="green" />
            <StatCard icon="Clock" label="Pending" value={money(comm.pending)} />
          </div>
          {comm.commissions.length === 0 ? <p className="text-slate-400">No commissions yet.</p> : (
            <div className="space-y-3">
              {comm.commissions.map((c) => (
                <div key={c.id} className="card-dark rounded-xl p-4 flex items-center justify-between">
                  <div>
                    <div className="text-white font-mono-fin font-bold">{money(c.amount)}</div>
                    <div className="text-slate-500 text-xs capitalize">{c.type} · {c.rate}%</div>
                  </div>
                  <StatusBadge status={c.status} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {tab === "team" && <TeamTree />}
      {tab === "payouts" && <PayoutsPanel />}
    </DashboardLayout>
  );
}

function AddClient({ onDone }) {
  const [form, setForm] = useState({ applicant_name: "", applicant_phone: "", loan_type: "", amount: "", tenure_months: "36" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.applicant_name || !form.loan_type || !form.amount) { toast.error("Fill client name, loan type & amount"); return; }
    setLoading(true);
    try {
      await agentAddClient({ ...form, amount: Number(form.amount), tenure_months: Number(form.tenure_months) });
      toast.success("Client application added!");
      onDone();
    } catch { toast.error("Could not add client"); } finally { setLoading(false); }
  };
  return (
    <div className="max-w-xl" data-testid="add-client">
      <h2 className="font-heading text-2xl font-bold text-white mb-5">Add a Client</h2>
      <form onSubmit={submit} className="card-dark rounded-2xl p-6 space-y-4" data-testid="add-client-form">
        <Input data-testid="client-name" placeholder="Client Name *" value={form.applicant_name} onChange={(e) => set("applicant_name", e.target.value)} className="bg-obsidian-900/60 border-white/10 h-11 text-white" />
        <Input data-testid="client-phone" placeholder="Client Phone" value={form.applicant_phone} onChange={(e) => set("applicant_phone", e.target.value)} className="bg-obsidian-900/60 border-white/10 h-11 text-white" />
        <Select value={form.loan_type} onValueChange={(v) => set("loan_type", v)}>
          <SelectTrigger data-testid="client-loantype" className="bg-obsidian-900/60 border-white/10 h-11 text-white"><SelectValue placeholder="Loan Type" /></SelectTrigger>
          <SelectContent className="bg-obsidian-700 border-white/10 text-white">
            {LOAN_TYPES.map((t) => <SelectItem key={t} value={t} className="focus:bg-gold/20 focus:text-white">{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="grid grid-cols-2 gap-4">
          <Input data-testid="client-amount" type="number" placeholder="Amount (₹)" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="bg-obsidian-900/60 border-white/10 h-11 text-white" />
          <Input data-testid="client-tenure" type="number" placeholder="Tenure (mo)" value={form.tenure_months} onChange={(e) => set("tenure_months", e.target.value)} className="bg-obsidian-900/60 border-white/10 h-11 text-white" />
        </div>
        <Button type="submit" disabled={loading} data-testid="add-client-submit" className="w-full gold-gradient text-obsidian-900 font-bold h-11 rounded-full">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Add Client"}
        </Button>
      </form>
    </div>
  );
}
